import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Sun,
  ShoppingBasket,
  TreeDeciduous,
  Sparkles,
  Music,
  Backpack,
  Calendar,
  Clock,
  MapPin,
  ArrowRight,
  ExternalLink,
  Star,
  Heart,
  Users,
  Quote,
  Check,
  Bell,
  Ticket,
  Store,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import PageShell from '../components/PageShell';
import SEO from '../components/SEO';
import GivebutterModal, { GivebutterScript } from '../components/GivebutterModal';
import {
  summerSeriesEvents,
  ssHeroWide,
  summerGalleryImages,
  PHOTO_CEILI,
  PHOTO_LEADERSHIP,
  PHOTO_MURAL,
  VENUE_COLLAGE,
  joyVoices,
  behindTheJoyImages,
} from '../mock';
import { useI18n } from '../contexts/I18nContext';
import publicApi, { resolveMediaUrl, formatTimeRange } from '../lib/publicApi';
import { JoyUnderline, JoySticker, JoyDotsField } from '../components/JoyAccents';

const TICKETS_WIDGET = 'gBZ6E6';
const VENDORS_WIDGET = 'prWrye';
const SPONSORSHIP_WIDGET = 'g8M7VR';

// Sponsorship levels — sourced from Givebutter (g8M7VR).
// All buttons open the same sponsorship widget in a modal.
// Copy comes from i18n via key: ss.sponsor.<key>.name / .desc
const SPONSORSHIP_LEVELS = [
  { key: 'l1', emoji: '🌱', amount: '$250', color: 'teal' },
  { key: 'l2', emoji: '🎨', amount: '$500', color: 'coral' },
  { key: 'l3', emoji: '🤝', amount: '$1,000', color: 'yellow' },
  { key: 'l4', emoji: '🎶', amount: '$2,500', color: 'coral' },
  { key: 'l5', emoji: '☀️', amount: '$5,000', color: 'teal', featured: true },
  { key: 'l6', emoji: '💎', amount: '$10,000+', color: 'yellow' },
];

const iconMap = { Sun, ShoppingBasket, TreeDeciduous, Sparkles, Music, Backpack };
const colorMap = {
  coral: { bg: '#E5614C', soft: 'bg-[#E5614C]/15', solid: 'bg-[#E5614C]' },
  teal: { bg: '#5DCAB5', soft: 'bg-[#5DCAB5]/20', solid: 'bg-[#5DCAB5]' },
  yellow: { bg: '#FCD968', soft: 'bg-[#FCD968]/40', solid: 'bg-[#FCD968]' },
};

const realPhotos = [PHOTO_CEILI, PHOTO_LEADERSHIP, PHOTO_MURAL, VENUE_COLLAGE];

// Rotate icons/colors for live events that don't pre-define them
const FALLBACK_ICONS = ['Sun', 'ShoppingBasket', 'TreeDeciduous', 'Sparkles', 'Music', 'Backpack'];
const FALLBACK_COLORS = ['coral', 'teal', 'yellow'];

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

function formatHumanDate(start, end) {
  if (!start) return '';
  const [, m, d] = start.split('-');
  const startStr = `${MONTH_NAMES[parseInt(m, 10) - 1] || ''} ${parseInt(d, 10)}`;
  if (end && end !== start) {
    const [, em, ed] = end.split('-');
    if (em === m) return `${MONTH_NAMES[parseInt(m, 10) - 1]} ${parseInt(d, 10)} – ${parseInt(ed, 10)}`;
    return `${startStr} – ${MONTH_NAMES[parseInt(em, 10) - 1] || ''} ${parseInt(ed, 10)}`;
  }
  return startStr;
}

function normalizeForSummerCard(ev, index) {
  const color = (ev.category?.color && FALLBACK_COLORS.includes(ev.category.color))
    ? ev.category.color
    : FALLBACK_COLORS[index % FALLBACK_COLORS.length];
  return {
    id: ev.id || ev.slug || `live-${index}`,
    icon: FALLBACK_ICONS[index % FALLBACK_ICONS.length],
    color,
    title: ev.title,
    date: ev.display_date || formatHumanDate(ev.start_date, ev.end_date),
    time: formatTimeRange(ev.start_time, ev.end_time) || (ev.cost || ''),
    partner: ev.location || 'GEMS Programming',
    desc: ev.short_description || ev.description || '',
    featured: !!ev.is_featured,
    rawImage: resolveMediaUrl(ev.featured_image),
    registerUrl: ev.register_url || '',
    location: ev.location || '',
    createdAt: ev.created_at || ev.updated_at || '',
  };
}

export default function SummerSeriesPage() {
  const { t } = useI18n();
  const [liveEvents, setLiveEvents] = useState(null);
  const [ticketsOpen, setTicketsOpen] = useState(false);
  const [sponsorOpen, setSponsorOpen] = useState(false);

  useEffect(() => {
    let mounted = true;
    publicApi
      .get('/events', { params: { status: 'published', category: 'summer-series', limit: 50 } })
      .then((res) => {
        if (!mounted) return;
        const list = (res.data || []).map(normalizeForSummerCard);
        setLiveEvents(list);
      })
      .catch(() => {
        if (mounted) setLiveEvents([]);
      });
    return () => {
      mounted = false;
    };
  }, []);

  const allEvents = useMemo(() => {
    if (liveEvents && liveEvents.length > 0) return liveEvents;
    return summerSeriesEvents;
  }, [liveEvents]);

  const usingLive = liveEvents && liveEvents.length > 0;

  const festival = allEvents.find((e) => e.featured) || allEvents.find((e) => e.id === 'festival');
  const others = allEvents.filter((e) => e !== festival);

  // ItemList JSON-LD of all visible series events for AEO/SEO
  const eventListJsonLd = useMemo(() => {
    const items = allEvents.filter(Boolean).slice(0, 12).map((e, i) => {
      const startDate = e.start_date || e.startDate || (e.month && e.day ? `2026-${String(['JAN','FEB','MAR','APR','MAY','JUN','JUL','AUG','SEP','OCT','NOV','DEC'].indexOf(e.month) + 1).padStart(2,'0')}-${String(e.day).padStart(2,'0')}` : undefined);
      return {
        '@type': 'ListItem',
        position: i + 1,
        item: {
          '@type': 'Event',
          name: e.title || e.name,
          ...(startDate ? { startDate } : {}),
          eventStatus: 'https://schema.org/EventScheduled',
          eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
          location: {
            '@type': 'Place',
            name: e.location || 'GEMS @ The Arts',
            address: {
              '@type': 'PostalAddress',
              streetAddress: '160 Hollis Street',
              addressLocality: 'Framingham',
              addressRegion: 'MA',
              postalCode: '01702',
              addressCountry: 'US',
            },
          },
          organizer: { '@type': 'Organization', name: 'GEMS @ The Arts', url: 'https://gemsatthearts.org/' },
          offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD', availability: 'https://schema.org/InStock', url: 'https://gemsatthearts.org/summer-series' },
          description: e.description || e.desc || 'Brighter Futures Summer Series event in Framingham, MA.',
        },
      };
    });
    return {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: 'Brighter Futures Summer Series 2026',
      itemListElement: items,
    };
  }, [allEvents]);

  // Surface the 2 most recently added events to populate "What's New" lineup updates.
  const recentlyAdded = useMemo(() => {
    if (!usingLive) return [];
    return [...allEvents]
      .filter((e) => e !== festival && e.createdAt)
      .sort((a, b) => (b.createdAt > a.createdAt ? 1 : -1))
      .slice(0, 2);
  }, [allEvents, festival, usingLive]);

  return (
    <PageShell
      eyebrow={<><Sun className="h-4 w-4" /> {t('ss.eyebrow')}</>}
      title={<>{t('ss.title1')} <span className="italic text-[#5DCAB5]">{t('ss.title2')}</span></>}
      subtitle={t('ss.subtitle')}
      accent="coral"
    >
      <SEO
        title="Brighter Futures Summer Series 2026"
        description="A season-long Framingham, MA celebration of music, arts, food, and community joy. Featuring the Brighter Futures Music Festival, Mobile Market Mondays, daily park enrichment, the Joy Kick Off Night, and our Back to School Extravaganza."
        path="/summer-series"
        jsonLd={eventListJsonLd}
      />

      {/* Load the Givebutter widgets script once for the whole page */}
      <GivebutterScript />

      {/* Modal embeds for Tickets + Sponsorship */}
      <GivebutterModal
        open={ticketsOpen}
        onOpenChange={setTicketsOpen}
        widgetId={TICKETS_WIDGET}
        title="Purchase tickets — Brighter Futures Summer Series"
      />
      <GivebutterModal
        open={sponsorOpen}
        onOpenChange={setSponsorOpen}
        widgetId={SPONSORSHIP_WIDGET}
        title="Become a sponsor — Brighter Futures Summer Series"
      />

      {/* ─── Season Archive ribbon ─────────────────────────────── */}
      <section className="pt-2 pb-4">
        <div className="max-w-6xl mx-auto px-5 md:px-10">
          <div
            className="rounded-2xl bg-[#FAF3EE] border border-[#E5614C]/20 px-5 py-3.5 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-[13.5px]"
            data-testid="ss-archive-ribbon"
          >
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white text-[#E5614C] text-[11px] font-bold uppercase tracking-[0.16em] border border-[#E5614C]/20">
              {t('ss.archiveLabel')}
            </span>
            <span className="text-[#2A1B1A]/80">
              {t('ss.archiveBody')} <Link to="/festival" className="text-[#E5614C] font-semibold hover:underline">{t('ss.archiveFestivalLink')}</Link> · <Link to="/#events" className="text-[#E5614C] font-semibold hover:underline">{t('ss.archiveLink')}</Link>
            </span>
          </div>
        </div>
      </section>

      {/* ─── Cinematic immersive hero ─────────────────────────────── */}
      <section className="pb-14" data-testid="ss-immersive-hero">
        <div className="max-w-6xl mx-auto px-5 md:px-10">
          <div className="relative rounded-[32px] overflow-hidden shadow-[0_50px_100px_-30px_rgba(42,27,26,0.5)] group">
            <img
              src={ssHeroWide}
              alt="Crowd celebrating live music at the Brighter Futures Summer Series festival"
              className="w-full h-[420px] lg:h-[600px] object-cover group-hover:scale-[1.04] transition-transform duration-[1.6s]"
            />
            {/* Multi-layer overlay for depth */}
            <div className="absolute inset-0 bg-gradient-to-t from-[#2A1B1A]/85 via-[#2A1B1A]/35 to-transparent" />
            <div className="absolute inset-0 bg-gradient-to-tr from-[#E5614C]/20 via-transparent to-[#5DCAB5]/20 mix-blend-overlay" />

            {/* Floating sticker accents */}
            <div className="absolute top-6 right-6 lg:top-10 lg:right-10 flex flex-col items-end gap-3 z-10">
              <JoySticker rotate={6} color="yellow">{t('ss.hero.sticker1')}</JoySticker>
              <JoySticker rotate={-4} color="white" className="hidden sm:inline-flex">{t('ss.hero.sticker2')}</JoySticker>
            </div>

            {/* Bottom band */}
            <div className="absolute bottom-6 left-6 right-6 lg:bottom-10 lg:left-10 lg:right-10 flex flex-wrap gap-3 items-end justify-between">
              <div className="flex flex-wrap gap-2">
                <span className="px-4 py-2 rounded-full bg-[#FCD968] text-[#2A1B1A] font-semibold text-sm shadow-md">{t('ss.badge1')}</span>
                <span className="px-4 py-2 rounded-full bg-white text-[#2A1B1A] font-semibold text-sm shadow-md">{t('ss.badge2')}</span>
                <span className="px-4 py-2 rounded-full bg-[#5DCAB5] text-white font-semibold text-sm shadow-md">{t('ss.badge3')}</span>
              </div>
              <Button
                onClick={() => setTicketsOpen(true)}
                className="rounded-full bg-[#E5614C] hover:bg-[#d04b36] text-white font-semibold h-12 px-6 shadow-[0_10px_24px_-6px_rgba(229,97,76,0.6)]"
                data-testid="ss-hero-tickets-btn"
              >
                <Ticket className="h-4 w-4 mr-2" /> {t('ss.getTickets')}
              </Button>
            </div>

            {/* Subtle bottom-edge stat strip */}
            <div className="absolute -bottom-px inset-x-0 hidden lg:flex justify-center pointer-events-none">
              <div className="bg-[#FAF3EE] rounded-t-2xl px-8 py-3 flex items-center gap-6 text-[12px] font-semibold text-[#2A1B1A]/70 uppercase tracking-[0.16em] shadow-[0_-10px_30px_-10px_rgba(0,0,0,0.15)]">
                <span><span className="text-[#E5614C]">5</span> {t('ss.stats.weekly')}</span>
                <span className="hidden md:inline">·</span>
                <span><span className="text-[#5DCAB5]">2</span> {t('ss.stats.festival')}</span>
                <span className="hidden md:inline">·</span>
                <span><span className="text-[#FCD968] drop-shadow">∞</span> {t('ss.stats.reasons')}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Featured Festival magazine block ─────────────────────── */}
      {festival && (
        <section className="py-10">
          <div className="max-w-6xl mx-auto px-5 md:px-10">
            <div className="relative rounded-[28px] overflow-hidden bg-[#2A1B1A] text-white" data-testid="ss-featured-festival">
              <div className="absolute -top-32 -right-20 w-[400px] h-[400px] rounded-full bg-[#E5614C]/25 blur-3xl pointer-events-none" />
              <div className="absolute -bottom-32 -left-20 w-[400px] h-[400px] rounded-full bg-[#5DCAB5]/20 blur-3xl pointer-events-none" />
              <JoyDotsField className="opacity-[0.12] mix-blend-screen" />

              <div className="relative grid lg:grid-cols-12 gap-0">
                <div className="lg:col-span-7 p-8 lg:p-14">
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#FCD968] text-[#2A1B1A] text-[12px] font-semibold uppercase tracking-[0.18em]">
                    <Star className="h-3.5 w-3.5" fill="#2A1B1A" /> {t('ss.fest.badge')}
                  </div>
                  <h2 className="font-serif-display text-[40px] sm:text-[52px] lg:text-[64px] leading-[1.02] font-bold mt-5">
                    {usingLive ? festival.title : (
                      <>{t('ss.fest.title1')} <span className="italic text-[#FCD968]">{t('ss.fest.title2')}</span></>
                    )}
                  </h2>
                  <div className="mt-6 flex flex-wrap items-center gap-5 text-white/85">
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-[#FCD968]" /> {usingLive ? festival.date : t('ss.fest.date')}
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4 text-[#FCD968]" /> {usingLive ? (festival.time || t('ss.fest.time')) : t('ss.fest.time')}
                    </div>
                    <div className="flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-[#FCD968]" /> {usingLive ? (festival.location || t('ss.fest.loc')) : t('ss.fest.loc')}
                    </div>
                  </div>
                  <p className="mt-6 text-white/80 text-[16px] leading-relaxed max-w-xl">
                    {usingLive ? (festival.desc || t('ss.fest.desc')) : t('ss.fest.desc')}
                  </p>
                  <div className="mt-8 flex flex-wrap gap-3">
                    <Button
                      onClick={() => setTicketsOpen(true)}
                      className="rounded-full bg-[#E5614C] hover:bg-[#d04b36] text-white font-semibold h-12 px-7 btn-press"
                      data-testid="ss-festival-tickets-btn"
                    >
                      <Ticket className="h-4 w-4 mr-2" /> {t('ss.getTickets')}
                    </Button>
                    <Button
                      onClick={() => setSponsorOpen(true)}
                      variant="outline"
                      className="rounded-full border-2 border-white/40 text-white hover:bg-white hover:text-[#2A1B1A] bg-transparent font-semibold h-12 px-7 btn-press"
                      data-testid="ss-festival-sponsor-btn"
                    >
                      <Users className="h-4 w-4 mr-2" /> {t('ss.becomeSponsor')}
                    </Button>
                  </div>
                </div>
                <div className="lg:col-span-5 relative">
                  <img
                    src={(usingLive && festival.rawImage) ? festival.rawImage : summerGalleryImages[1]}
                    alt={festival?.title || 'Brighter Futures Music Festival'}
                    className="w-full h-[280px] lg:h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-l from-transparent to-[#2A1B1A]/40 lg:to-[#2A1B1A]/0 lg:from-[#2A1B1A]/20" />
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ─── What's New / Recently Added ──────────────────────────── */}
      {recentlyAdded.length > 0 && (
        <section className="py-8" data-testid="ss-whats-new">
          <div className="max-w-6xl mx-auto px-5 md:px-10">
            <div className="rounded-2xl bg-white border border-[#FCD968]/40 px-5 sm:px-7 py-5 flex flex-wrap items-center gap-x-6 gap-y-3">
              <div className="inline-flex items-center gap-2 text-[12px] font-bold uppercase tracking-[0.18em] text-[#E5614C]">
                <Bell className="h-3.5 w-3.5" /> {t('ss.justAdded')}
              </div>
              <div className="flex flex-wrap gap-x-5 gap-y-2 flex-1 min-w-0">
                {recentlyAdded.map((e) => (
                  <div key={e.id} className="flex items-center gap-2 text-[13.5px] text-[#2A1B1A]/85 min-w-0">
                    <span className="h-1.5 w-1.5 rounded-full bg-[#5DCAB5] shrink-0" />
                    <span className="font-semibold truncate">{e.title}</span>
                    {e.date && <span className="text-[#2A1B1A]/45">· {e.date}</span>}
                  </div>
                ))}
              </div>
              <a
                href="#lineup"
                className="text-[13px] font-semibold text-[#E5614C] hover:underline inline-flex items-center gap-1 shrink-0"
              >
                {t('ss.seeFullLineup')} <ArrowRight className="h-3.5 w-3.5" />
              </a>
            </div>
          </div>
        </section>
      )}

      {/* ─── Lineup of Joy ────────────────────────────────────────── */}
      <section id="lineup" className="py-16">
        <div className="max-w-6xl mx-auto px-5 md:px-10">
          <div className="text-center max-w-2xl mx-auto">
            <div className="inline-flex items-center gap-2 text-[#E5614C] font-semibold uppercase tracking-[0.18em] text-[12px]">
              <span className="h-[2px] w-8 bg-[#E5614C]" /> {t('ss.lineup.eyebrow')}
            </div>
            <h2 className="font-serif-display text-[34px] sm:text-[44px] lg:text-[56px] font-bold text-[#2A1B1A] mt-4 leading-[1.04]">
              {t('ss.lineup.title1')}{' '}
              <JoyUnderline color="#E5614C">{t('ss.lineup.title2')}</JoyUnderline>
            </h2>
            <p className="mt-5 text-[16px] text-[#2A1B1A]/70">{t('ss.lineup.subtitle')}</p>
          </div>

          <div className="mt-12 grid md:grid-cols-2 gap-5" data-testid="ss-lineup-grid">
            {others.map((e, idx) => {
              const Icon = iconMap[e.icon] || Sun;
              const cl = colorMap[e.color] || colorMap.coral;
              return (
                <article
                  key={e.id || idx}
                  data-testid={`ss-event-${e.id || idx}`}
                  className="group relative bg-white rounded-[24px] p-7 lg:p-8 border border-[#2A1B1A]/8 hover:border-transparent hover:shadow-[0_30px_60px_-25px_rgba(42,27,26,0.3)] hover:-translate-y-1.5 transition-[transform,box-shadow,border-color] duration-300 overflow-hidden"
                >
                  <div className={`absolute -top-12 -right-12 w-32 h-32 rounded-full ${cl.soft} opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />
                  <div className="relative flex items-start gap-5">
                    <div className={`h-14 w-14 rounded-2xl ${cl.solid} ${e.color === 'yellow' ? 'text-[#2A1B1A]' : 'text-white'} flex items-center justify-center shrink-0 shadow-[0_10px_24px_-8px_rgba(0,0,0,0.2)]`}>
                      <Icon className="h-6 w-6" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-serif-display text-[22px] lg:text-[26px] font-bold text-[#2A1B1A] leading-tight">{e.title}</h3>
                      <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1.5 text-[13px] text-[#2A1B1A]/70">
                        {e.date && <span className="inline-flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5" /> {e.date}</span>}
                        {e.time && <span className="inline-flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> {e.time}</span>}
                      </div>
                      {e.partner && (
                        <div className="mt-2.5 text-[12px] font-semibold uppercase tracking-[0.14em]" style={{ color: cl.bg }}>
                          {e.partner}
                        </div>
                      )}
                      <p className="mt-3 text-[14.5px] text-[#2A1B1A]/70 leading-relaxed">{e.desc}</p>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
          {liveEvents === null && (
            <div className="mt-6 text-center text-[13px] text-[#2A1B1A]/45" data-testid="ss-loading-hint">Loading the latest lineup…</div>
          )}
        </div>
      </section>

      {/* ─── Behind the Joy — voices + moments ────────────────────── */}
      <section className="py-16 lg:py-20 bg-white relative overflow-hidden" data-testid="ss-behind-the-joy">
        <JoyDotsField className="opacity-[0.12]" />
        <div className="relative max-w-6xl mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3 flex-wrap">
              <span className="inline-flex items-center gap-2 text-[#5DCAB5] font-semibold uppercase tracking-[0.18em] text-[12px]">
                <span className="h-[2px] w-8 bg-[#5DCAB5]" /> {t('ss.behind.eyebrow')}
              </span>
              <JoySticker rotate={-6} color="coral">{t('ss.behind.sticker')}</JoySticker>
            </div>
            <h2 className="font-serif-display mt-5 text-[36px] sm:text-[44px] lg:text-[52px] leading-[1.04] font-bold text-[#2A1B1A]">
              {t('ss.behind.title1')} <JoyUnderline color="#FCD968">{t('ss.behind.title2')}</JoyUnderline>.
            </h2>
            <p className="mt-5 text-[16px] text-[#2A1B1A]/70 leading-relaxed">
              {t('ss.behind.body')}
            </p>
            <div className="mt-7 space-y-4">
              {joyVoices.map((v) => (
                <figure
                  key={v.slot}
                  className="relative bg-[#FAF3EE] rounded-2xl p-5 border border-[#2A1B1A]/8"
                  data-testid={`joy-voice-${v.slot}`}
                >
                  <Quote className="absolute top-4 right-4 h-5 w-5 text-[#E5614C]/30" />
                  <blockquote className="text-[15px] text-[#2A1B1A] leading-relaxed font-medium">
                    {t(`ss.voices.${v.slot}.quote`)}
                  </blockquote>
                  <figcaption className="mt-3 text-[12px] uppercase tracking-[0.14em] font-semibold text-[#2A1B1A]/55">
                    {t(`ss.voices.${v.slot}.name`)} · <span className="text-[#E5614C]">{t(`ss.voices.${v.slot}.role`)}</span>
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>

          {/* Moments grid */}
          <div className="lg:col-span-7">
            <div className="grid grid-cols-6 gap-3">
              <div className="col-span-4 row-span-2 h-[260px] sm:h-[380px] rounded-2xl overflow-hidden shadow-[0_24px_50px_-25px_rgba(42,27,26,0.4)]">
                <img src={behindTheJoyImages[0]} alt="Live music moment at GEMS @ The Arts" className="w-full h-full object-cover hover:scale-[1.04] transition-transform duration-[1.4s]" />
              </div>
              <div className="col-span-2 h-[125px] sm:h-[182px] rounded-2xl overflow-hidden shadow-[0_18px_40px_-20px_rgba(42,27,26,0.3)]">
                <img src={behindTheJoyImages[1]} alt="Community gathering" className="w-full h-full object-cover hover:scale-[1.04] transition-transform duration-[1.4s]" />
              </div>
              <div className="col-span-2 h-[125px] sm:h-[190px] rounded-2xl overflow-hidden shadow-[0_18px_40px_-20px_rgba(42,27,26,0.3)]">
                <img src={behindTheJoyImages[2]} alt="Youth in motion at a GEMS summer event" className="w-full h-full object-cover hover:scale-[1.04] transition-transform duration-[1.4s]" />
              </div>
              <div className="col-span-6 h-[140px] sm:h-[180px] rounded-2xl overflow-hidden shadow-[0_18px_40px_-20px_rgba(42,27,26,0.3)]">
                <img src={behindTheJoyImages[3]} alt="Outdoor festival moment in Framingham" className="w-full h-full object-cover hover:scale-[1.02] transition-transform duration-[1.4s]" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Vendors: become a vendor at our markets ──────────────── */}
      <section id="vendors" className="py-16 lg:py-20 bg-[#FAF3EE]" data-testid="ss-vendors">
        <div className="max-w-6xl mx-auto px-5 md:px-10">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
            <div className="lg:col-span-5">
              <div className="inline-flex items-center gap-2 text-[#5DCAB5] font-semibold uppercase tracking-[0.18em] text-[12px]">
                <Store className="h-3.5 w-3.5" /> {t('ss.vendors.eyebrow')}
              </div>
              <h2 className="font-serif-display mt-4 text-[34px] sm:text-[44px] lg:text-[52px] font-bold text-[#2A1B1A] leading-[1.04]">
                {t('ss.vendors.title1')} <JoyUnderline color="#5DCAB5">{t('ss.vendors.title2')}</JoyUnderline>.
              </h2>
              <div className="mt-5 space-y-4 text-[15.5px] text-[#2A1B1A]/75 leading-relaxed">
                <p>
                  {t('ss.vendors.body1_a')} <strong className="text-[#2A1B1A]">{t('ss.vendors.body1_b')}</strong> {t('ss.vendors.body1_c')}
                </p>
                <p>
                  {t('ss.vendors.body2')}
                </p>
              </div>

              <div className="mt-7 rounded-2xl bg-white border border-[#2A1B1A]/8 p-5">
                <div className="text-[11px] font-semibold tracking-[0.18em] text-[#5DCAB5] uppercase">{t('ss.vendors.eventDetails')}</div>
                <div className="mt-3 flex items-start gap-2.5 text-[14.5px] text-[#2A1B1A]">
                  <MapPin className="h-4 w-4 mt-0.5 text-[#5DCAB5] shrink-0" />
                  <div>
                    <div className="font-semibold">{t('ss.vendors.venueName')}</div>
                    <div className="text-[#2A1B1A]/70">{t('ss.vendors.venueAddr')}</div>
                  </div>
                </div>
                <div className="mt-4 flex items-start gap-2.5 text-[14.5px] text-[#2A1B1A]">
                  <Calendar className="h-4 w-4 mt-0.5 text-[#E5614C] shrink-0" />
                  <div>
                    <div className="font-semibold">{t('ss.vendors.dateLabel')}</div>
                    <div className="text-[#2A1B1A]/70">{t('ss.vendors.satDate')}</div>
                    <div className="text-[#2A1B1A]/70">{t('ss.vendors.sunDate')}</div>
                  </div>
                </div>
              </div>

              <div className="mt-5 rounded-2xl bg-white border border-[#2A1B1A]/8 p-5 space-y-5">
                <div>
                  <div className="text-[11px] font-semibold tracking-[0.18em] text-[#5DCAB5] uppercase">{t('ss.vendors.includesLabel')}</div>
                  <p className="mt-2.5 text-[14.5px] text-[#2A1B1A]/75 leading-relaxed">
                    {t('ss.vendors.includesBody')}
                  </p>
                </div>
                <div className="border-t border-[#2A1B1A]/8 pt-5">
                  <div className="text-[11px] font-semibold tracking-[0.18em] text-[#5DCAB5] uppercase">{t('ss.vendors.expectLabel')}</div>
                  <p className="mt-2.5 text-[14.5px] text-[#2A1B1A]/75 leading-relaxed">
                    {t('ss.vendors.expectBody1')}
                  </p>
                  <p className="mt-3 text-[14.5px] text-[#2A1B1A]/75 leading-relaxed">
                    {t('ss.vendors.expectBody2')}
                  </p>
                </div>
              </div>

              <p className="mt-7 text-[13px] text-[#2A1B1A]/55">
                {t('ss.vendors.questions_a')}{' '}
                <a href="mailto:info@gemsatthearts.org" className="text-[#E5614C] font-semibold hover:underline">info@gemsatthearts.org</a>{' '}— {t('ss.vendors.questions_b')}
              </p>
            </div>

            {/* Embedded Vendors widget */}
            <div className="lg:col-span-7" data-testid="ss-vendors-widget-mount">
              <div className="rounded-[24px] bg-white border border-[#2A1B1A]/8 p-3 sm:p-5 shadow-[0_30px_60px_-30px_rgba(42,27,26,0.25)] min-h-[480px]">
                <givebutter-widget id={VENDORS_WIDGET}></givebutter-widget>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Sponsorship & supporter levels ───────────────────────── */}
      <section id="sponsorship" className="py-20 relative" data-testid="ss-sponsorship">
        <div className="max-w-6xl mx-auto px-5 md:px-10">
          <div className="text-center max-w-2xl mx-auto">
            <div className="inline-flex items-center gap-2 text-[#E5614C] font-semibold uppercase tracking-[0.18em] text-[12px]">
              <span className="h-[2px] w-8 bg-[#E5614C]" /> {t('ss.sponsor.eyebrow')}
            </div>
            <h2 className="font-serif-display text-[34px] sm:text-[44px] lg:text-[56px] font-bold text-[#2A1B1A] mt-4 leading-[1.04]">
              {t('ss.sponsor.title1')} <JoyUnderline color="#5DCAB5">{t('ss.sponsor.title2')}</JoyUnderline>.
            </h2>
            <p className="mt-5 text-[16px] text-[#2A1B1A]/70">
              {t('ss.sponsor.subtitle')}
            </p>
          </div>

          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
            {SPONSORSHIP_LEVELS.map((tier) => {
              const cl = colorMap[tier.color] || colorMap.coral;
              const isFeatured = !!tier.featured;
              const name = t(`ss.sponsor.${tier.key}.name`);
              const desc = t(`ss.sponsor.${tier.key}.desc`);
              return (
                <div
                  key={tier.key}
                  data-testid={`sponsorship-tier-${tier.key}`}
                  className={`relative rounded-[24px] p-7 lg:p-8 transition-[transform,box-shadow] duration-300 hover:-translate-y-1.5 ${
                    isFeatured
                      ? 'bg-[#2A1B1A] text-white shadow-[0_40px_70px_-25px_rgba(42,27,26,0.6)] lg:scale-[1.04]'
                      : 'bg-white text-[#2A1B1A] border border-[#2A1B1A]/8 hover:shadow-[0_28px_55px_-25px_rgba(42,27,26,0.3)]'
                  }`}
                >
                  {isFeatured && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <JoySticker rotate={-4} color="yellow">{t('ss.sponsor.mostPopular')}</JoySticker>
                    </div>
                  )}
                  <div className="text-[34px] leading-none mb-3" aria-hidden="true">{tier.emoji}</div>
                  <h3 className="font-serif-display text-[22px] lg:text-[26px] font-bold leading-tight">
                    {name}
                  </h3>
                  <div className={`mt-1 font-serif-display text-[32px] font-bold ${isFeatured ? 'text-[#FCD968]' : ''}`} style={!isFeatured ? { color: cl.bg } : undefined}>
                    {tier.amount}
                  </div>
                  <p className={`mt-4 text-[14.5px] leading-relaxed ${isFeatured ? 'text-white/85' : 'text-[#2A1B1A]/75'}`}>
                    {desc}
                  </p>
                  <Button
                    onClick={() => setSponsorOpen(true)}
                    className={`mt-7 w-full rounded-full h-11 font-semibold btn-press ${
                      isFeatured
                        ? 'bg-[#E5614C] hover:bg-[#d04b36] text-white'
                        : 'bg-[#2A1B1A] hover:bg-[#1f1414] text-white'
                    }`}
                    data-testid={`sponsor-cta-${tier.key}`}
                  >
                    {t('ss.sponsor.cta')} <ArrowRight className="h-4 w-4 ml-1.5" />
                  </Button>
                </div>
              );
            })}
          </div>

          <p className="mt-10 text-center text-[14px] text-[#2A1B1A]/55 max-w-2xl mx-auto">
            {t('ss.sponsor.custom_a')}{' '}
            <a href="mailto:info@gemsatthearts.org" className="text-[#E5614C] font-semibold hover:underline">info@gemsatthearts.org</a>{' '}
            {t('ss.sponsor.custom_b')}
          </p>
        </div>
      </section>

      {/* ─── Past moments gallery (real photos) ───────────────────── */}
      <section className="py-14">
        <div className="max-w-6xl mx-auto px-5 md:px-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {realPhotos.map((src, i) => (
              <div key={i} className="aspect-square rounded-2xl overflow-hidden shadow-[0_18px_40px_-20px_rgba(42,27,26,0.3)] hover:-translate-y-1 transition-transform duration-300">
                <img src={src} alt={`GEMS @ The Arts community moment ${i + 1}`} className="w-full h-full object-cover" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Final CTA ────────────────────────────────────────────── */}
      <section className="py-16 pb-24">
        <div className="max-w-5xl mx-auto px-5 md:px-10">
          <div className="relative rounded-[28px] bg-[#E5614C] text-white p-10 lg:p-14 text-center overflow-hidden">
            <div className="absolute -top-20 left-1/2 -translate-x-1/2 w-[500px] h-[500px] rounded-full bg-white/10 blur-3xl pointer-events-none" />
            <JoyDotsField className="opacity-[0.18] mix-blend-screen" />

            <h2 className="relative font-serif-display text-[36px] lg:text-[52px] font-bold leading-[1.04]">
              {t('ss.cta.title1')} <span className="italic">{t('ss.cta.title2')}</span> {t('ss.cta.title3')}
            </h2>
            <p className="relative mt-5 text-white/90 text-[16px] max-w-xl mx-auto">{t('ss.cta.desc')}</p>
            <div className="relative mt-8 flex flex-wrap justify-center gap-3">
              <Button
                onClick={() => setTicketsOpen(true)}
                className="rounded-full bg-white text-[#E5614C] hover:bg-[#FAF3EE] font-semibold h-12 px-7 btn-press"
                data-testid="ss-final-tickets-btn"
              >
                <Ticket className="h-4 w-4 mr-2" /> {t('ss.getTickets')}
              </Button>
              <Button asChild variant="outline" className="rounded-full border-2 border-white/60 text-white hover:bg-white hover:text-[#E5614C] bg-transparent font-semibold h-12 px-7 btn-press">
                <Link to="/get-involved">{t('ss.cta.btn1')} <ArrowRight className="h-4 w-4 ml-2" /></Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Sticky "Get Tickets" removed — Summer 2026 is archived and not selling. */}
    </PageShell>
  );
}
